import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TreePine, Eye, Ear, Hand, Wind, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function NatureWalkGuide() {
  const navigate = useNavigate();

  const benefits = [
    { stat: "21%", desc: "reduction in cortisol (stress hormone)" },
    { stat: "28%", desc: "improvement in mood" },
    { stat: "15%", desc: "boost in creative thinking" },
    { stat: "12%", desc: "lower blood pressure" }
  ];

  const senses = [
    { sense: "See", icon: Eye, prompt: "5 things you see", examples: "A bird, sunlight through leaves, a flower, clouds, tree bark texture" },
    { sense: "Hear", icon: Ear, prompt: "4 things you hear", examples: "Birds singing, wind in trees, water flowing, your footsteps" },
    { sense: "Touch", icon: Hand, prompt: "3 things you feel", examples: "Bark texture, cool breeze, sun warmth, ground beneath feet" },
    { sense: "Smell", icon: Wind, prompt: "2 things you smell", examples: "Fresh air, flowers, earth, rain, pine" },
    { sense: "Internal", icon: Heart, prompt: "1 thing you're grateful for", examples: "Your health, this moment, nature's beauty" }
  ];

  const mindfulWalkingTips = [
    "Leave your phone on silent in your pocket",
    "Walk slightly slower than your usual pace",
    "Notice the sensation of each foot touching the ground",
    "Take deep breaths and notice the air quality",
    "Pause when something catches your attention",
    "If your mind wanders, gently bring it back to your senses",
    "Try walking in silence for at least 10 minutes"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
            Week 4 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Nature Walk
          </h1>
          <p className="text-xl text-secondary-600">Forest Bathing for Body and Soul</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <TreePine className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Practice: 45 Minutes in Nature</h3>
              <p className="text-green-100 text-lg">
                Walk in a park, trail, or tree-lined neighborhood. Leave your phone on silent. 
                This isn't about exercise—it's about presence and connection with the natural world.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">The Science of Nature</h2>
          <p className="text-secondary-700 mb-4">
            The Japanese practice of "Shinrin-yoku" (forest bathing) has been studied extensively. 
            Just 20 minutes in nature starts to lower stress hormones. Here's what research shows:
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {benefits.map((item, i) => (
              <div key={i} className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-3xl font-bold text-green-600">{item.stat}</p>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 5-4-3-2-1 Grounding */}
        <section className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">The 5-4-3-2-1 Grounding Exercise</h2>
          <p className="text-secondary-600 mb-4">During your walk, use this mindfulness technique to fully connect with nature:</p>
          <div className="space-y-4">
            {senses.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex items-start gap-4">
                <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-green-700">{item.prompt}</h3>
                  <p className="text-sm text-secondary-600 italic">Examples: {item.examples}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Mindful Walking Tips */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Mindful Walking Tips</h2>
          <ul className="space-y-3">
            {mindfulWalkingTips.map((tip, i) => (
              <li key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg text-secondary-700">
                <TreePine className="w-5 h-5 text-green-500 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default NatureWalkGuide;
